const PRIORITY_ORDER = {
  high: 0,
  medium: 1,
  watch: 2,
  low: 2,
};

const PRODUCTION_TIME_ZONE = "Europe/Paris";
const MINIMUM_SIGNIFICANT_DAILY_GAIN = 15;
const MINIMUM_DAILY_SHARE = 0.25;
const MAXIMUM_PRODUCTION_DAYS = 3;
const MINIMUM_SIGNIFICANT_COMPACTION_GAIN = 25;
/** Cap kept above the 3 visible cards so the Synthèse row can scroll horizontally. */
const MAX_RECOMMENDATIONS = 6;

/** Thresholds used by activity-detail recommendations (cycle level). */
export const ACTIVITY_RECOMMENDATION_THRESHOLDS = {
  SUB_OPTIMAL_FILLING: 65,
  FILLING_OBJECTIVE: 80,
  HIGH_IMPACT_GAP: 10,
  EARLY_COLLECTION: 60,
  LATE_OVERFLOW: 95,
  SUGGESTED_TRIGGER: 75,
  CONTAMINANT_WATCH: 5,
  CONTAMINANT_MEDIUM: 15,
  CONTAMINANT_HIGH: 30,
};

const toFiniteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

export const getCollectionLevel = (historyCycle) =>
  toFiniteNumber(historyCycle?.[0]?.filling_level);

export const getCollectionMassKg = (historyCycle, clientRegisteredWeight) => {
  const registeredWeightTonnes = toFiniteNumber(clientRegisteredWeight);
  if (registeredWeightTonnes != null && registeredWeightTonnes > 0) {
    return registeredWeightTonnes * 1000;
  }

  return toFiniteNumber(historyCycle?.[0]?.mass);
};

export const getContaminantRate = (
  totalVolumeSortingError,
  totalWasteVolume,
) => {
  const sortingErrorVolume = toFiniteNumber(totalVolumeSortingError);
  const wasteVolume = toFiniteNumber(totalWasteVolume);

  if (sortingErrorVolume == null || wasteVolume == null || wasteVolume <= 0) {
    return null;
  }

  return Math.round((sortingErrorVolume * 1000) / wasteVolume) / 10;
};

export const getCompactionGain = (historyCycle) => {
  const gains = (historyCycle || [])
    .filter((reading) => reading?.packmat === true)
    .map((reading) => toFiniteNumber(reading?.diff_filling))
    .filter((gain) => gain != null);

  if (gains.length === 0) return null;

  return Math.round(gains.reduce((total, gain) => total + gain, 0) * 10) / 10;
};

const roundToOneDecimal = (value) => Math.round(value * 10) / 10;

const getLocalDayKey = (time) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: PRODUCTION_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(time));
  const values = Object.fromEntries(
    parts.map((part) => [part.type, part.value]),
  );

  return `${values.year}-${values.month}-${values.day}`;
};

export const getFillingKeyMoments = (historyCycle, targetLevel) => {
  const readings = (historyCycle || [])
    .map((reading) => ({
      source: reading,
      time: reading?._time,
      level: toFiniteNumber(reading?.filling_level),
    }))
    .filter((reading) => reading.time && reading.level != null)
    .sort((first, second) => new Date(first.time) - new Date(second.time));

  if (readings.length === 0) {
    return {
      readingCount: 0,
      productionDays: [],
      significantCompactions: [],
      collection: null,
    };
  }

  const dailyProduction = new Map();

  for (let index = 1; index < readings.length; index += 1) {
    const variation = readings[index].level - readings[index - 1].level;
    if (variation <= 0 || readings[index].source?.packmat === true) continue;

    const dayKey = getLocalDayKey(readings[index].time);
    const currentDay = dailyProduction.get(dayKey) || {
      dayKey,
      gain: 0,
      peakGain: 0,
      peakTime: readings[index].time,
    };
    currentDay.gain += variation;
    if (variation > currentDay.peakGain) {
      currentDay.peakGain = variation;
      currentDay.peakTime = readings[index].time;
    }
    dailyProduction.set(dayKey, currentDay);
  }

  const totalProductionGain = Array.from(dailyProduction.values()).reduce(
    (total, day) => total + day.gain,
    0,
  );
  const productionDays = Array.from(dailyProduction.values())
    .filter(
      (day) =>
        day.gain >= MINIMUM_SIGNIFICANT_DAILY_GAIN &&
        day.gain >= totalProductionGain * MINIMUM_DAILY_SHARE,
    )
    .sort((first, second) => second.gain - first.gain)
    .slice(0, MAXIMUM_PRODUCTION_DAYS)
    .sort((first, second) => first.dayKey.localeCompare(second.dayKey))
    .map((day) => ({
      ...day,
      gain: roundToOneDecimal(day.gain),
      peakGain: roundToOneDecimal(day.peakGain),
    }));

  const significantCompactions = readings
    .filter((reading) => reading.source?.packmat === true)
    .map((reading) => ({
      time: reading.time,
      gain: toFiniteNumber(reading.source?.diff_filling),
    }))
    .filter(
      (compaction) =>
        compaction.gain != null &&
        compaction.gain > MINIMUM_SIGNIFICANT_COMPACTION_GAIN,
    )
    .map((compaction) => ({
      ...compaction,
      gain: roundToOneDecimal(compaction.gain),
    }));

  const lastReading = readings[readings.length - 1];
  const target = toFiniteNumber(targetLevel);
  const difference =
    target == null ? null : roundToOneDecimal(target - lastReading.level);
  const collection = {
    level: roundToOneDecimal(lastReading.level),
    target,
    difference,
    status:
      target == null
        ? "withoutTarget"
        : difference > 15
          ? "belowTarget"
          : difference > 0
            ? "slightlyBelowTarget"
            : "targetReached",
  };

  return {
    readingCount: readings.length,
    productionDays,
    significantCompactions,
    collection,
  };
};

export const ACTIVITY_KEY_MOMENT_THRESHOLDS = {
  COMPACTION_GAIN: MINIMUM_SIGNIFICANT_COMPACTION_GAIN,
  PRODUCTION_DAILY_GAIN: MINIMUM_SIGNIFICANT_DAILY_GAIN,
  SORTING_ERRORS: 15,
};

export const ACTIVITY_KEY_MOMENT_CATALOG = [
  {
    id: "arrival",
    type: "arrival",
    titleKey: "activityKeyMomentGuide.arrival.title",
    criterionKey: "activityKeyMomentGuide.arrival.criterion",
  },
  {
    id: "departure",
    type: "departure",
    titleKey: "activityKeyMomentGuide.departure.title",
    criterionKey: "activityKeyMomentGuide.departure.criterion",
  },
  {
    id: "compaction",
    type: "compaction",
    titleKey: "activityKeyMomentGuide.compaction.title",
    criterionKey: "activityKeyMomentGuide.compaction.criterion",
  },
  {
    id: "production",
    type: "production",
    titleKey: "activityKeyMomentGuide.production.title",
    criterionKey: "activityKeyMomentGuide.production.criterion",
  },
  {
    id: "sortingPeak",
    type: "sortingPeak",
    titleKey: "activityKeyMomentGuide.sortingPeak.title",
    criterionKey: "activityKeyMomentGuide.sortingPeak.criterion",
  },
];

/**
 * Key moments for the Synthèse panel (always includes arrival + departure).
 */
export const buildActivitySummaryKeyMoments = (historyCycle) => {
  const readings = (historyCycle || [])
    .map((reading) => ({
      source: reading,
      time: reading?._time,
      level: toFiniteNumber(reading?.filling_level),
      anomalies: toFiniteNumber(reading?.anomalies) ?? 0,
    }))
    .filter((reading) => reading.time)
    .sort((first, second) => new Date(first.time) - new Date(second.time));

  if (readings.length === 0) {
    return [];
  }

  const moments = [];
  const firstReading = readings[0];
  const lastReading = readings[readings.length - 1];

  moments.push({
    id: "arrival",
    type: "arrival",
    time: firstReading.time,
    descriptionKey: "activityKeyMoment.arrival",
    descriptionParams: {
      level: firstReading.level == null ? "-" : Math.round(firstReading.level),
    },
  });

  const dailyProduction = new Map();
  for (let index = 1; index < readings.length; index += 1) {
    if (readings[index].level == null || readings[index - 1].level == null) {
      continue;
    }
    const variation = readings[index].level - readings[index - 1].level;
    if (variation <= 0 || readings[index].source?.packmat === true) continue;

    const dayKey = getLocalDayKey(readings[index].time);
    const currentDay = dailyProduction.get(dayKey) || {
      dayKey,
      gain: 0,
      peakGain: 0,
      peakTime: readings[index].time,
    };
    currentDay.gain += variation;
    if (variation > currentDay.peakGain) {
      currentDay.peakGain = variation;
      currentDay.peakTime = readings[index].time;
    }
    dailyProduction.set(dayKey, currentDay);
  }

  const totalProductionGain = Array.from(dailyProduction.values()).reduce(
    (total, day) => total + day.gain,
    0,
  );
  Array.from(dailyProduction.values())
    .filter(
      (day) =>
        day.gain >= MINIMUM_SIGNIFICANT_DAILY_GAIN &&
        day.gain >= totalProductionGain * MINIMUM_DAILY_SHARE,
    )
    .sort((first, second) => second.gain - first.gain)
    .slice(0, MAXIMUM_PRODUCTION_DAYS)
    .sort((first, second) => first.dayKey.localeCompare(second.dayKey))
    .forEach((day) => {
      moments.push({
        id: `production-${day.dayKey}`,
        type: "production",
        time: day.peakTime,
        descriptionKey: "activityKeyMoment.production",
        descriptionParams: {
          gain: roundToOneDecimal(day.gain),
        },
      });
    });

  readings
    .filter((reading) => reading.source?.packmat === true)
    .forEach((reading) => {
      const gain = toFiniteNumber(reading.source?.diff_filling);
      if (gain == null || gain <= MINIMUM_SIGNIFICANT_COMPACTION_GAIN) return;
      moments.push({
        id: `compaction-${reading.time}`,
        type: "compaction",
        time: reading.time,
        descriptionKey: "activityKeyMoment.compaction",
        descriptionParams: {
          gain: roundToOneDecimal(gain),
        },
      });
    });

  const sortingPeak = readings.reduce((best, reading) => {
    if (reading.anomalies <= ACTIVITY_KEY_MOMENT_THRESHOLDS.SORTING_ERRORS) {
      return best;
    }
    if (!best || reading.anomalies > best.anomalies) {
      return reading;
    }
    return best;
  }, null);

  if (sortingPeak) {
    moments.push({
      id: `sorting-${sortingPeak.time}`,
      type: "sortingPeak",
      time: sortingPeak.time,
      descriptionKey: "activityKeyMoment.sortingPeak",
      descriptionParams: {
        count: Math.round(sortingPeak.anomalies),
      },
    });
  }

  moments.push({
    id: "departure",
    type: "departure",
    time: lastReading.time,
    descriptionKey: "activityKeyMoment.departure",
    descriptionParams: {
      level: lastReading.level == null ? "-" : Math.round(lastReading.level),
    },
  });

  return moments.sort(
    (first, second) => new Date(first.time) - new Date(second.time),
  );
};

const severityToPriority = (severity) =>
  severity === "watch" ? "low" : severity;

const estimateMonthlyRotationsSaved = (collectionLevel, suggestedTrigger) => {
  const gap = suggestedTrigger - collectionLevel;
  if (gap <= 0) return 1;
  return Math.max(1, Math.round((gap / 10) * 4));
};

const buildRecommendation = ({
  id,
  type,
  severity,
  scope,
  titleKey,
  titleParams,
  impactKey,
  impactParams,
}) => ({
  id,
  type,
  severity,
  scope,
  titleKey,
  titleParams,
  impactKey,
  impactParams,
  // Legacy fields for scoped tab lists
  key: titleKey,
  priority: severityToPriority(severity),
  values: { ...titleParams, ...impactParams },
});

/**
 * Catalog of recommendation types for the methodology page (thresholds + messages).
 * Keep in sync with buildActivityDetailRecommendations.
 */
export const ACTIVITY_RECOMMENDATION_CATALOG = [
  {
    id: "raise-trigger",
    type: "raise_trigger",
    severity: "high",
    scope: "filling",
    scenarioTitleKey: "activityRecoGuide.raiseTrigger.scenarioTitle",
    scenarioDescriptionKey:
      "activityRecoGuide.raiseTrigger.scenarioDescription",
    criterionKey: "activityRecoGuide.raiseTrigger.criterion",
    exampleKey: "activityRecoGuide.raiseTrigger.example",
    recommendationKey: "activityRecoGuide.raiseTrigger.recommendation",
    impactKey: "activityRecoGuide.raiseTrigger.impact",
  },
  {
    id: "late-overflow",
    type: "late_overflow",
    severity: "high",
    scope: "filling",
    scenarioTitleKey: "activityRecoGuide.lateOverflow.scenarioTitle",
    scenarioDescriptionKey:
      "activityRecoGuide.lateOverflow.scenarioDescription",
    criterionKey: "activityRecoGuide.lateOverflow.criterion",
    exampleKey: "activityRecoGuide.lateOverflow.example",
    recommendationKey: "activityRecoGuide.lateOverflow.recommendation",
    impactKey: "activityRecoGuide.lateOverflow.impact",
  },
  {
    id: "near-objective",
    type: "near_objective",
    severity: "watch",
    scope: "filling",
    scenarioTitleKey: "activityRecoGuide.nearObjective.scenarioTitle",
    scenarioDescriptionKey:
      "activityRecoGuide.nearObjective.scenarioDescription",
    criterionKey: "activityRecoGuide.nearObjective.criterion",
    exampleKey: "activityRecoGuide.nearObjective.example",
    recommendationKey: "activityRecoGuide.nearObjective.recommendation",
    impactKey: "activityRecoGuide.nearObjective.impact",
  },
  {
    id: "sorting-high",
    type: "sorting_high",
    severity: "high",
    scope: "sorting",
    scenarioTitleKey: "activityRecoGuide.sortingHigh.scenarioTitle",
    scenarioDescriptionKey: "activityRecoGuide.sortingHigh.scenarioDescription",
    criterionKey: "activityRecoGuide.sortingHigh.criterion",
    exampleKey: "activityRecoGuide.sortingHigh.example",
    recommendationKey: "activityRecoGuide.sortingHigh.recommendation",
    impactKey: "activityRecoGuide.sortingHigh.impact",
  },
  {
    id: "sorting-medium",
    type: "sorting_medium",
    severity: "medium",
    scope: "sorting",
    scenarioTitleKey: "activityRecoGuide.sortingMedium.scenarioTitle",
    scenarioDescriptionKey:
      "activityRecoGuide.sortingMedium.scenarioDescription",
    criterionKey: "activityRecoGuide.sortingMedium.criterion",
    exampleKey: "activityRecoGuide.sortingMedium.example",
    recommendationKey: "activityRecoGuide.sortingMedium.recommendation",
    impactKey: "activityRecoGuide.sortingMedium.impact",
  },
  {
    id: "sorting-watch",
    type: "sorting_watch",
    severity: "watch",
    scope: "sorting",
    scenarioTitleKey: "activityRecoGuide.sortingWatch.scenarioTitle",
    scenarioDescriptionKey:
      "activityRecoGuide.sortingWatch.scenarioDescription",
    criterionKey: "activityRecoGuide.sortingWatch.criterion",
    exampleKey: "activityRecoGuide.sortingWatch.example",
    recommendationKey: "activityRecoGuide.sortingWatch.recommendation",
    impactKey: "activityRecoGuide.sortingWatch.impact",
  },
  {
    id: "follow-up",
    type: "follow_up",
    severity: "high",
    scope: "followUp",
    scenarioTitleKey: "activityRecoGuide.followUp.scenarioTitle",
    scenarioDescriptionKey: "activityRecoGuide.followUp.scenarioDescription",
    criterionKey: "activityRecoGuide.followUp.criterion",
    exampleKey: "activityRecoGuide.followUp.example",
    recommendationKey: "activityRecoGuide.followUp.recommendation",
    impactKey: "activityRecoGuide.followUp.impact",
  },
  {
    id: "compaction-data",
    type: "compaction_data",
    severity: "watch",
    scope: "compaction",
    scenarioTitleKey: "activityRecoGuide.compactionData.scenarioTitle",
    scenarioDescriptionKey:
      "activityRecoGuide.compactionData.scenarioDescription",
    criterionKey: "activityRecoGuide.compactionData.criterion",
    exampleKey: "activityRecoGuide.compactionData.example",
    recommendationKey: "activityRecoGuide.compactionData.recommendation",
    impactKey: "activityRecoGuide.compactionData.impact",
  },
];

export const buildActivityDetailRecommendations = ({
  historyCycle,
  totalVolumeSortingError,
  totalWasteVolume,
  statusKey,
  packmatActive,
  numberOfPackmat,
}) => {
  const recommendations = [];
  const {
    SUB_OPTIMAL_FILLING,
    FILLING_OBJECTIVE,
    HIGH_IMPACT_GAP,
    EARLY_COLLECTION,
    LATE_OVERFLOW,
    SUGGESTED_TRIGGER,
    CONTAMINANT_WATCH,
    CONTAMINANT_MEDIUM,
    CONTAMINANT_HIGH,
  } = ACTIVITY_RECOMMENDATION_THRESHOLDS;

  const collectionLevel = getCollectionLevel(historyCycle);
  const contaminantRate = getContaminantRate(
    totalVolumeSortingError,
    totalWasteVolume,
  );

  if (collectionLevel != null && collectionLevel < SUB_OPTIMAL_FILLING) {
    const gapToObjective = FILLING_OBJECTIVE - collectionLevel;
    const severity =
      collectionLevel < EARLY_COLLECTION || gapToObjective > HIGH_IMPACT_GAP
        ? "high"
        : "medium";
    const rotationsSaved = estimateMonthlyRotationsSaved(
      collectionLevel,
      SUGGESTED_TRIGGER,
    );

    recommendations.push(
      buildRecommendation({
        id: "raise-trigger",
        type: "raise_trigger",
        severity,
        scope: "filling",
        titleKey: "activityDetailRecommendationRaiseTrigger",
        titleParams: {
          level: Math.round(collectionLevel),
          currentTrigger: SUB_OPTIMAL_FILLING,
          suggestedTrigger: SUGGESTED_TRIGGER,
        },
        impactKey: "activityDetailRecommendationRaiseTriggerImpact",
        impactParams: { rotationsSaved },
      }),
    );
  } else if (collectionLevel != null && collectionLevel >= LATE_OVERFLOW) {
    recommendations.push(
      buildRecommendation({
        id: "late-overflow",
        type: "late_overflow",
        severity: "high",
        scope: "filling",
        titleKey: "activityDetailRecommendationLateOverflow",
        titleParams: { level: Math.round(collectionLevel) },
        impactKey: "activityDetailRecommendationLateOverflowImpact",
        impactParams: { level: Math.round(collectionLevel) },
      }),
    );
  } else if (
    collectionLevel != null &&
    collectionLevel >= SUB_OPTIMAL_FILLING &&
    collectionLevel < FILLING_OBJECTIVE
  ) {
    const gap = Math.round(FILLING_OBJECTIVE - collectionLevel);
    recommendations.push(
      buildRecommendation({
        id: "near-objective",
        type: "near_objective",
        severity: gap > HIGH_IMPACT_GAP / 2 ? "medium" : "watch",
        scope: "filling",
        titleKey: "activityDetailRecommendationNearObjective",
        titleParams: {
          level: Math.round(collectionLevel),
          objective: FILLING_OBJECTIVE,
          gap,
        },
        impactKey: "activityDetailRecommendationNearObjectiveImpact",
        impactParams: { gap, objective: FILLING_OBJECTIVE },
      }),
    );
  }

  if (contaminantRate != null && contaminantRate > CONTAMINANT_WATCH) {
    const severity =
      contaminantRate > CONTAMINANT_HIGH
        ? "high"
        : contaminantRate > CONTAMINANT_MEDIUM
          ? "medium"
          : "watch";
    recommendations.push(
      buildRecommendation({
        id: `sorting-${severity}`,
        type: `sorting_${severity === "watch" ? "watch" : severity}`,
        severity,
        scope: "sorting",
        titleKey: "activityDetailRecommendationSortingAction",
        titleParams: { rate: contaminantRate },
        impactKey: "activityDetailRecommendationSortingImpact",
        impactParams: { rate: contaminantRate },
      }),
    );
  }

  if (["warning", "interrogation", "none"].includes(statusKey)) {
    const severity = statusKey === "none" ? "medium" : "high";
    recommendations.push(
      buildRecommendation({
        id: "follow-up",
        type: "follow_up",
        severity,
        scope: "followUp",
        titleKey: "activityDetailRecommendationFollowUpAction",
        titleParams: {},
        impactKey: "activityDetailRecommendationFollowUpImpact",
        impactParams: {},
      }),
    );
  }

  const compactionGain = getCompactionGain(historyCycle);
  if (packmatActive && numberOfPackmat > 0 && compactionGain == null) {
    recommendations.push(
      buildRecommendation({
        id: "compaction-data",
        type: "compaction_data",
        severity: "watch",
        scope: "compaction",
        titleKey: "activityDetailRecommendationCompactionDataAction",
        titleParams: {},
        impactKey: "activityDetailRecommendationCompactionDataImpact",
        impactParams: {},
      }),
    );
  }

  return recommendations
    .sort(
      (first, second) =>
        (PRIORITY_ORDER[first.severity] ?? 99) -
        (PRIORITY_ORDER[second.severity] ?? 99),
    )
    .slice(0, MAX_RECOMMENDATIONS);
};
